import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme, alpha } from '@mui/material/styles';
import { Box, Stack, Drawer, Typography } from '@mui/material';
// @mui icons (MUI icons are pre-compiled and fully local, avoiding CORS issues)
import {
  GridViewRounded,
  AccountBalanceWalletOutlined,
  WifiOutlined,
  PhoneAndroidOutlined,
  TvOutlined,
  FlashOnOutlined,
  SchoolOutlined,
  HistoryOutlined,
  CardGiftcardOutlined,
  BarChartOutlined,
  PersonOutlineOutlined,
  CodeOutlined,
  PhoneOutlined,
  ExitToAppOutlined,
  GroupWorkOutlined,
  EventRepeatOutlined,
} from '@mui/icons-material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';

// routes
import { PATH_DASHBOARD, PATH_PAGE } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();
  const { logout } = useAuth();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  // Redesigned navigation list matching user request and screenshot
  const navConfig = [
    {
      items: [
        {
          title: 'Dashboard',
          path: PATH_DASHBOARD.general.app,
          icon: <GridViewRounded sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Wallet',
          path: PATH_DASHBOARD.general.walletsummary,
          icon: <AccountBalanceWalletOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Buy Data',
          path: PATH_DASHBOARD.general.buydata,
          icon: <WifiOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Buy Airtime',
          path: PATH_DASHBOARD.general.buyairtime,
          icon: <PhoneAndroidOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'TV Cable Sub',
          path: PATH_DASHBOARD.general.buycable,
          icon: <TvOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Pay Electricity Bill',
          path: PATH_DASHBOARD.general.buybill,
          icon: <FlashOnOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Result Checker',
          path: PATH_DASHBOARD.general.exam,
          icon: <SchoolOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Transactions',
          path: PATH_DASHBOARD.general.transactions,
          icon: <HistoryOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Sales Analysis',
          path: PATH_PAGE.comingSoon,
          icon: <BarChartOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Profile & Settings',
          path: PATH_DASHBOARD.user.account,
          icon: <PersonOutlineOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Developer Tools',
          path: PATH_DASHBOARD.general.apidocs,
          icon: <CodeOutlined sx={{ fontSize: 22 }} />,
        },
        {
          title: 'Contact us',
          path: PATH_DASHBOARD.general.support,
          icon: <PhoneOutlined sx={{ fontSize: 22 }} />,
        },
      ],
    },
  ];

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Centered Circular Logo */}
      <Stack
        spacing={2}
        sx={{
          pt: 2.5,
          pb: 1.5,
          px: 2.5,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Logo isLight={!pathname.includes('/secure')} />
      </Stack>

      {/* Vertical Navigation Sections (Admin items are removed to match screenshot exactly) */}
      <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Styled Logout Button at the bottom */}
      {!isCollapse && (
        <Stack spacing={1} sx={{ p: 2, pb: 2.5 }}>
          <Box
            onClick={logout}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              cursor: 'pointer',
              color: '#FF4D4F',
              marginLeft: theme.spacing(1.5),
              marginRight: theme.spacing(1.5),
              '&:hover': { bgcolor: alpha('#FF4D4F', 0.08) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <ExitToAppOutlined sx={{ fontSize: 24, color: '#FF4D4F' }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Logout
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              bgcolor: 'background.default',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRight: 'none',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
